import React, {ChangeEvent, FC, FormEvent, useRef, useState} from "react";
import {
    Divider,
    IconButton,
    InputAdornment,
    InputBase,
    InputBaseProps,
    Typography
} from "@mui/material";
import Paper from "@mui/material/Paper/Paper";
import EnterIcon from "../icons/EnterIcon";
import CrossIcon from "../icons/CrossIcon";
import {styled} from "@mui/material/styles";

interface InstructionProps {
    instruction: string;
    instructionSubmitted: (value: string) => void;
    setInstruction: (value: string) => void;
    isGenerating: boolean;
    placeholderText?: string;
    disableIcon?: boolean;
    minRows?: number;
    maxRows?: number;
    disabled?: boolean;
}

const isQuestion = (instruction: string): boolean => {
    return instruction.toLowerCase().startsWith('what ') ||
        instruction.toLowerCase().startsWith('why ') ||
        instruction.toLowerCase().startsWith('who ') ||
        instruction.toLowerCase().startsWith('when ') ||
        instruction.toLowerCase().startsWith('where ') ||
        instruction.toLowerCase().startsWith('how ') ||
        instruction.toLowerCase().startsWith('can ') ||
        instruction.toLowerCase().startsWith('could ') ||
        instruction.toLowerCase().startsWith('should ') ||
        instruction.toLowerCase().startsWith('would ') ||
        instruction.toLowerCase().startsWith('will ') ||
        instruction.toLowerCase().startsWith('do ') ||
        instruction.toLowerCase().startsWith('does ') ||
        instruction.toLowerCase().startsWith('did ') ||
        instruction.toLowerCase().startsWith('was ') ||
        instruction.toLowerCase().startsWith('is ') ||
        instruction.toLowerCase().startsWith('are ') ||
        instruction.toLowerCase().startsWith('am ') ||
        instruction.toLowerCase().startsWith('shall ') ||
        instruction.toLowerCase().startsWith('may ') ||
        instruction.toLowerCase().startsWith('might ') ||
        instruction.toLowerCase().startsWith('must ') ||
        instruction.toLowerCase().startsWith('was ') ||
        instruction.toLowerCase().startsWith('were ');
}

const enhanceFormatting = (instruction: string, isSubmitting: boolean = false) => {
    const lines = instruction.split('\n')
    // If it's a question, add a question mark
    if (lines.length > 1) {
        if (isQuestion(lines[0])) {
            if (!lines[0].includes('?') && !lines[0].includes('.') && !lines[0].includes('!') && !lines[0].includes('-') && !lines[0].includes(':') && !lines[0].endsWith(';') && !lines[0].endsWith(',')) {
                lines[0] = lines[0] + '?';
            } else if (lines[0].endsWith('.') && !lines[0].includes('?')) {
                lines[0] = lines[0].slice(0, -1) + '?';
            }
        } else {
            if (!lines[0].includes('?') && !lines[0].includes('.') && !lines[0].includes('!') && !lines[0].includes('-') && !lines[0].includes(':') && !lines[0].endsWith(';') && !lines[0].endsWith(',')) {
                lines[0] = lines[0] + '.';
            }
        }
    }
    if (isSubmitting) {
        if (isQuestion(lines[lines.length - 1])) {
            if (!lines[lines.length - 1].includes('?') && !lines[lines.length - 1].includes('.') && !lines[lines.length - 1].includes('!') && !lines[lines.length - 1].includes('-') && !lines[lines.length - 1].includes(':') && !lines[lines.length - 1].endsWith(';') && !lines[lines.length - 1].endsWith(',')) {
                lines[lines.length - 1] = lines[lines.length - 1] + '?';
            } else if (lines[lines.length - 1].endsWith('.') && !lines[lines.length - 1].includes('?')) {
                lines[lines.length - 1] = lines[lines.length - 1].slice(0, -1) + '?';
            }
        } else {
            if (!lines[lines.length - 1].endsWith('.') && !lines[lines.length - 1].endsWith('?') && !lines[lines.length - 1].endsWith('!') && !lines[lines.length - 1].endsWith('-') && !lines[lines.length - 1].endsWith(':') && !lines[lines.length - 1].endsWith(';') && !lines[lines.length - 1].endsWith(',')) {
                lines[lines.length - 1] = lines[lines.length - 1] + '.';
            }
        }
        lines[lines.length - 1] = lines[lines.length - 1].charAt(0).toUpperCase() + lines[lines.length - 1].slice(1);
    }

    let _inst = lines.join('\n');

    // Make first letter capital
    _inst = _inst.charAt(0).toUpperCase() + _inst.slice(1);

    return _inst;
}

const CustomInputBase = styled((props: InputBaseProps) => (
    <InputBase
        {...props}
    />
))(({ theme }) => ({
    '& .MuiInputBase-input': {
        transition: 'height 1s'
    },
}));
const Instruction: FC<InstructionProps> = ({
                                               instruction,
                                               instructionSubmitted,
                                               setInstruction,
                                               isGenerating,
                                               placeholderText,
                                               disableIcon,
                                               minRows,
                                               maxRows,
                                               disabled
                                           }) => {
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef(null);
    const [showWarning, setShowWarning] = useState(false);



    return (
        <div>
            <Paper style={{
                borderRadius: "8px",
                boxShadow: '0px 1px 2px 1px #0F1A2A14 inset',
                background: isFocused ? 'linear-gradient(270deg, #E28A9C 0%, #D378F2 38.54%, #5A9CFF 100%)' : '#F6F8FC',
                minHeight: 40,
                gap: 8,
            }}
            >
                <form
                    style={{padding: 2}}
                    onSubmit={(e: FormEvent<HTMLFormElement>) => {
                        e.preventDefault();
                        instructionSubmitted(instruction);
                    }}>
                    <CustomInputBase
                        id="instruction"
                        inputRef={inputRef}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        multiline
                        disabled={disabled}
                        autoFocus
                        minRows={minRows ? minRows : 1}
                        maxRows={maxRows ? maxRows : 8}
                        onInput={(e: ChangeEvent<HTMLInputElement>) => {
                            setInstruction(e.target.value);
                        }}
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' && !e.shiftKey && !e.altKey) {
                                e.preventDefault();
                                if (isGenerating) {
                                    setShowWarning(true);
                                } else {
                                    if (instruction.length <= 0) {
                                        setShowWarning(true);
                                        return;
                                    }
                                    setShowWarning(false)
                                    const _inst = instruction
                                    // const _inst = enhanceFormatting(instruction, true);
                                    setInstruction(_inst);
                                    instructionSubmitted(_inst);
                                }
                            } else {
                                if (e.code === 'Space' || (e.key === 'Enter' && e.shiftKey)) {
                                    const _inst = instruction
                                    // const _inst = enhanceFormatting(instruction)
                                    setInstruction(_inst);
                                }
                            }
                        }}
                        style={{
                            paddingLeft: 12, paddingTop: 2, paddingBottom: 3, paddingRight: 12,
                            background: '#F6F8FC',
                            borderRadius: 8,
                            minHeight: 36
                        }}
                        value={instruction}
                        placeholder={placeholderText ? placeholderText : "Ask a question or give an instruction about this transcript"}
                        size="small"
                        fullWidth
                        endAdornment={
                            disableIcon ? null : (
                                <InputAdornment position="end" style={{
                                    display: 'flex',
                                    alignSelf: 'flex-start',
                                    marginBottom: 'auto',
                                    marginTop: 16
                                }}>
                                    {instruction && (
                                        <IconButton onClick={(e) => {
                                            e.preventDefault();
                                            setInstruction('');
                                            if (inputRef.current) {
                                                const ele: HTMLInputElement = inputRef.current;
                                                ele.focus();
                                            }
                                        }}>
                                            <CrossIcon/>
                                        </IconButton>
                                    )
                                    }
                                    {instruction && (
                                        <Divider orientation={"vertical"} style={{
                                            height: 24,
                                            color: '#CBD4E1',
                                            marginRight: 8
                                        }}/>)
                                    }
                                    <EnterIcon active={isFocused}/>
                                </InputAdornment>)
                        }
                    />
                </form>
            </Paper>
            {isGenerating && showWarning && (
                <Typography variant={"caption"} style={{
                    color: '#f85f5f',
                    marginTop: 4,
                    marginLeft: 4,
                    marginRight: 4
                }}>Please wait for current generation to finish, or stop it to submit new instruction.</Typography>)
            }
            {instruction.length <= 0 && showWarning && (
                <Typography variant={"caption"} style={{
                    color: '#f85f5f',
                    marginTop: 4,
                    marginLeft: 4,
                    marginRight: 4
                }}>Instruction or message cannot be empty.</Typography>)
            }
        </div>
    )
        ;
};

export default Instruction;
